import { API_BASE_URL } from '../config';
import { apiFetch } from '../utils/apiFetch';
import type { ExecutionLog } from '../types/strategy';
import type { PageResponse } from '../types/pagination';

export interface RealizedTrade {
  id: number;
  trade_type: 'TAKE_PROFIT' | 'STOP_LOSS' | 'STRATEGY_SELL';
  symbol: string;
  quantity: number;
  profit_percent: number;
  profit_amount: number | null;
  executed_at: string;
}

export interface HistoryQuery {
  limit: number;
  offset: number;
  symbol?: string;
  action?: string;
  tradeType?: string;
  executed?: string;
  startDate?: string;
  endDate?: string;
  market?: 'ALL' | 'US' | 'KR';
  scope?: 'paper' | 'live';
}

export type KrSimulationDecision = ExecutionLog & {
  market: 'KR';
  run_id: string;
  confidence: number;
  weighted_total: number;
};

export type KrDecisionPage = PageResponse<KrSimulationDecision> & {
  market: 'KR';
  source: 'simulation_decisions';
  latest_run_id: string | null;
};

function appendRange(params: URLSearchParams, query: HistoryQuery) {
  params.append('limit', String(query.limit));
  params.append('offset', String(query.offset));
  if (query.startDate) params.append('start_date', query.startDate);
  if (query.endDate) params.append('end_date', query.endDate);
}

export async function getExecutionLogPage(
  query: HistoryQuery
): Promise<PageResponse<ExecutionLog>> {
  const params = new URLSearchParams();
  appendRange(params, query);
  if (query.symbol) params.append('symbol', query.symbol);
  if (query.action && query.action !== 'ALL') params.append('action', query.action);
  if (query.executed) params.append('executed', query.executed);

  const response = await apiFetch(`${API_BASE_URL}/api/strategies/logs?${params}`);
  if (!response.ok) throw new Error(`실행 이력 조회 실패: ${response.status}`);
  return response.json();
}

export async function getRealizedTradePage(
  query: HistoryQuery
): Promise<PageResponse<RealizedTrade>> {
  const params = new URLSearchParams();
  appendRange(params, query);
  if (query.tradeType && query.tradeType !== 'ALL') {
    params.append('trade_type', query.tradeType);
  }
  if (query.market && query.market !== 'ALL') params.append('market', query.market);
  if (query.scope) params.append('scope', query.scope);

  const response = await apiFetch(`${API_BASE_URL}/api/trades/history?${params}`);
  if (!response.ok) throw new Error(`실현 손익 조회 실패: ${response.status}`);
  return response.json();
}

export async function getKrSimulationDecisionPage(
  query: HistoryQuery
): Promise<KrDecisionPage> {
  const params = new URLSearchParams();
  appendRange(params, query);
  if (query.symbol) params.append('symbol', query.symbol);
  if (query.action && query.action !== 'ALL') params.append('action', query.action);

  const response = await apiFetch(`${API_BASE_URL}/api/simulations/history/decisions?${params}`);
  if (!response.ok) throw new Error(`KR Replay 판단 조회 실패: ${response.status}`);
  return response.json();
}
